import React, { useRef, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useTranslation, Trans } from "react-i18next";
import { useHistory } from "react-router";

import styles from "./PaymentForm.module.scss";

import InputMask from "./InputMask";
import Input from "./Input";
import { formatCardExpiry } from "../utils/formatCardExpiry";
import { ReactComponent as CvvIcon } from "../assets/cvvIcon.svg";
import LoadingSpinner from "./LoadingSpinner";
import Checkbox from "./Checkbox";
import { validateCardExpirationDate } from "../utils/validateCardExpirationDate";
import SuccessIndicator from "./SuccessIndicator";
import { useGlobalStore } from "../stores/globalStore";
import PhoneMaskInput from "./PhoneMaskInput";

function PaymentForm({
  onUpdateState,
  cardNumberRef,
  cardHolderRef,
  cardDateRef,
  onCardInputFocus,
  onCardInputBlur,
  cardCvv,
  children,
}) {
  const [mask, setMask] = useState("#### #### #### ####");
  const { t } = useTranslation();
  const cardInfoErrors = useRef("");
  const [isPaymentSuccessful, setIsPaymentSuccessful] = useState(false);
  const showShippingInfo = useGlobalStore((state) => state.showShippingInfo);
  const shippingDetailsErrors = useRef("");

  const history = useHistory();

  const handleFormChange = (event) => {
    const { name, value } = event.target;

    onUpdateState(name, value);
  };

  const onCardNumberChange = (values) => {
    let { formattedValue, value } = values;

    if (/^3[47]\d{0,13}$/.test(value)) {
      // amex
      setMask("#### ###### #####");
    } else if (/^3(?:0[0-5]|[68]\d)\d{0,11}$/.test(value)) {
      // diner's club, 14 digits
      setMask("#### ###### ####");
    } else if (/^\d{0,19}$/.test(value)) {
      // regular cc number, 16 digits
      setMask("#### #### #### #### ###");
    }

    onUpdateState("cardNumber", formattedValue);
  };

  const onCvvFocus = (event) => {
    onUpdateState("isCardFlipped", true);
  };

  const onCvvBlur = (event) => {
    onUpdateState("isCardFlipped", false);
  };

  function mergeCardInfoErrors(touched, errors) {
    if (
      touched.cardHolder &&
      errors.cardHolder &&
      !errors.cardHolder.includes(t("common.required"))
    ) {
      cardInfoErrors.current = errors.cardHolder;
      return true;
    } else if (
      touched.cardNumber &&
      errors.cardNumber &&
      !errors.cardNumber.includes(t("common.required"))
    ) {
      cardInfoErrors.current = errors.cardNumber;
      return true;
    } else if (
      touched.cardExpiry &&
      errors.cardExpiry &&
      !errors.cardExpiry.includes(t("common.required"))
    ) {
      cardInfoErrors.current = errors.cardExpiry;
      return true;
    } else if (
      touched.cardCvv &&
      errors.cardCvv &&
      !errors.cardCvv.includes(t("common.required"))
    ) {
      cardInfoErrors.current = errors.cardCvv;
      return true;
    } else {
      return false;
    }
  }

  function mergeShippingDetailsErrors(touched, errors) {
    if (
      touched.email &&
      errors.email &&
      !errors.email.includes(t("common.required"))
    ) {
      shippingDetailsErrors.current = errors.email;
      return true;
    } else if (
      touched.phone &&
      errors.phone &&
      !errors.phone.includes(t("common.required"))
    ) {
      shippingDetailsErrors.current = errors.phone;
      return true;
    } else {
      return false;
    }
  }

  return (
    <div className={styles.container}>
      <div className={`${styles.row} ${styles.cardWrapper}`}>{children}</div>

      <Formik
        initialValues={{
          cardNumber: "",
          cardHolder: "",
          cardExpiry: "",
          cardCvv: "",
          email: "",
          phone: "",
          shippingName: "",
          shippingAddress: "",
          termsAndConditions: false,
        }}
        validationSchema={Yup.object().shape({
          cardNumber: Yup.string()
            .min(15, t("common.incompleteCardNumber"))
            .required(t("common.required"))
            .test(
              "Is cardNumber valid?",
              t("common.incompleteCardNumber"),
              async (value) => {
                if (value) {
                  if (value.replace(/ /g, "").length < 8) {
                    return false;
                  } else {
                    return true;
                  }
                }
              }
            ),
          cardHolder: Yup.string().required(t("common.required")),
          cardExpiry: Yup.string()
            .min(7, t("common.incompleteExpirationDate"))
            .required(t("common.required"))
            .test(
              "Is CardExpiry outdated?",
              t("common.outdatedExpirationDate"),
              async (value) => {
                if (value?.length === 7) {
                  return validateCardExpirationDate(
                    value.substr(0, 2),
                    value.substr(-2)
                  );
                } else {
                  return true;
                }
              }
            ),
          cardCvv: Yup.string()
            .min(3, t("common.incompleteCvv"))
            .required(t("common.required")),
          email: Yup.string()
            .email(t("common.invalidEmail"))
            .required(t("common.required")),
          phone: Yup.string()
            .matches(/^[+]?[\d ]+$/, t("common.invalidPhone"))
            .min(7, t("common.invalidPhone"))
            .required(t("common.required")),
          shippingName: showShippingInfo
            ? Yup.string().required(t("common.required"))
            : undefined,
          shippingAddress: showShippingInfo
            ? Yup.string().required(t("common.required"))
            : undefined,
          termsAndConditions: Yup.bool()
            .oneOf([true], t("common.acceptAgreement"))
            .required(t("common.required")),
        })}
        onSubmit={async (values) => {
          console.log("Submiting");

          await new Promise((resolve) =>
            setTimeout(() => resolve(setIsPaymentSuccessful(true)), 2000)
          );

          // console.log(JSON.stringify(values, null, 2));

          // // setTimeout(() => {
          // //   history.push("/success");
          // // }, 1750);
        }}
      >
        {({ isSubmitting, errors, touched, values }) => (
          <Form className="">
            <div className={styles.row}>
              <h2 className={styles.heading}>
                {t("mainPage.paymentDetailsTitle")}
              </h2>
            </div>

            <div className={styles.row}>
              <div className={styles.fieldGroup}>
                <div className={styles.labelContainer}>
                  {/* <label htmlFor="cardHolder">
                    <span className={styles.label}>
                      {t("mainPage.cardHolder")}
                    </span>
                  </label> */}

                  <div
                    className={styles.error}
                    style={
                      touched.cardHolder &&
                      errors.cardHolder &&
                      touched.cardNumber &&
                      errors.cardNumber &&
                      touched.cardCvv &&
                      errors.cardCvv &&
                      touched.cardExpiry &&
                      errors.cardExpiry
                        ? { opacity: 1, transform: "translateY(0)" }
                        : { opacity: 0, transform: "translateY(100%)" }
                    }
                  >
                    {t("common.required")}
                  </div>
                </div>

                <div className={styles.formInputGroup}>
                  <div className={styles.formInputContainer}>
                    <div
                      className={`${styles.inputWrapper} ${styles.topChild}`}
                    >
                      <Input
                        className="CheckoutInput Input Input--empty"
                        autoComplete="ccname"
                        autoCorrect="off"
                        spellCheck="false"
                        placeholder={t("mainPage.cardHolder")}
                        id="cardHolder"
                        name="cardHolder"
                        type="text"
                        aria-invalid="false"
                        onChange={handleFormChange}
                        ref={cardHolderRef}
                        onFocus={(e) => onCardInputFocus(e, "cardHolder")}
                        onBlur={onCardInputBlur}
                        style={{
                          zIndex:
                            touched.cardHolder && errors.cardHolder
                              ? 1
                              : "unset",
                        }}
                      />
                    </div>

                    <div
                      className={`${styles.inputWrapper} ${styles.middleChild}`}
                    >
                      <InputMask
                        className="CheckoutInput CheckoutInput--tabularnums Input Input--empty"
                        autoComplete="cc-number"
                        autoCorrect="off"
                        spellCheck="false"
                        id="cardNumber"
                        name="cardNumber"
                        type="text"
                        inputMode="numeric"
                        aria-label="Card number"
                        placeholder="1234 1234 1234 1234"
                        aria-invalid="false"
                        onValueChange={onCardNumberChange}
                        format={mask}
                        ref={cardNumberRef}
                        onFocus={(e) => onCardInputFocus(e, "cardNumber")}
                        onBlur={onCardInputBlur}
                        style={{
                          zIndex:
                            touched.cardNumber && errors.cardNumber
                              ? 1
                              : "unset",
                        }}
                      />
                    </div>

                    <div
                      className={`${styles.inputWrapper} ${styles.bottomChild} ${styles.leftChild}`}
                    >
                      <InputMask
                        className="CheckoutInput CheckoutInput--tabularnums Input Input--empty"
                        autoComplete="cc-exp"
                        autoCorrect="off"
                        spellCheck="false"
                        id="cardExpiry"
                        name="cardExpiry"
                        type="text"
                        inputMode="numeric"
                        aria-label="Expiration"
                        placeholder={t("mainPage.expirationDatePlaceholder")}
                        aria-invalid="false"
                        format={formatCardExpiry}
                        mask={"MM / YY"}
                        onValueChange={({ formattedValue, value }) => {
                          onUpdateState(
                            "cardExpiry",
                            formattedValue.replaceAll(" ", "")
                          );
                        }}
                        ref={cardDateRef}
                        onFocus={(e) => onCardInputFocus(e, "cardDate")}
                        onBlur={onCardInputBlur}
                        style={{
                          zIndex:
                            touched.cardExpiry && errors.cardExpiry
                              ? 1
                              : "unset",
                        }}
                      />
                    </div>

                    <div
                      className={`${styles.inputWrapper} ${styles.bottomChild} ${styles.rightChild}`}
                    >
                      <Input
                        className="CheckoutInput CheckoutInput--tabularnums Input Input--empty"
                        autoComplete="cc-csc"
                        autoCorrect="off"
                        spellCheck="false"
                        id="cardCvv"
                        name="cardCvv"
                        type="text"
                        inputMode="numeric"
                        aria-label="CVV"
                        placeholder="CVV"
                        aria-invalid="false"
                        maxLength="4"
                        onChange={handleFormChange}
                        onFocus={onCvvFocus}
                        onBlur={onCvvBlur}
                        ref={cardCvv}
                        style={{
                          zIndex:
                            touched.cardCvv && errors.cardCvv ? 1 : "unset",
                        }}
                      />

                      <div className={styles.formFieldIcon}>
                        <CvvIcon />
                      </div>
                    </div>

                    <div
                      className={styles.fieldError}
                      style={
                        mergeCardInfoErrors(touched, errors)
                          ? { opacity: 1, height: 16 }
                          : { opacity: 0, height: 0 }
                      }
                    >
                      {cardInfoErrors.current}
                    </div>
                  </div>
                  {/* <div style="opacity: 1; transform: none;"></div> */}
                </div>
              </div>
            </div>

            <div className={styles.row}>
              <h2 className={styles.heading}>
                {t("mainPage.shippingAddressTitle")}
              </h2>
            </div>

            <div className={styles.row}>
              <div className={styles.fieldGroup}>
                <div className={styles.labelContainer}>
                  {/* <label htmlFor="phone">
                    <span className={styles.label}>{t("mainPage.phone")}</span>
                  </label> */}

                  <div
                    className={styles.error}
                    style={
                      touched.email &&
                      errors.email &&
                      touched.phone &&
                      errors.phone &&
                      errors.email.includes(t("common.required")) &&
                      errors.phone.includes(t("common.required"))
                        ? { opacity: 1, transform: "translateY(0)" }
                        : { opacity: 0, transform: "translateY(100%)" }
                    }
                  >
                    {t("common.required")}
                  </div>
                </div>

                <div className={styles.formInputGroup}>
                  <div className={styles.formInputContainer}>
                    <div
                      className={`${styles.inputWrapper} ${styles.topChild}`}
                    >
                      <Input
                        className="CheckoutInput Input Input--empty"
                        autoComplete="email"
                        autoCorrect="off"
                        spellCheck="false"
                        id="email"
                        name="email"
                        type="text"
                        inputMode="email"
                        autoCapitalize="none"
                        aria-invalid="false"
                        aria-describedby=""
                        placeholder={t("mainPage.email")}
                        style={{
                          zIndex: touched.email && errors.email ? 1 : "unset",
                        }}
                      />
                    </div>

                    <div
                      className={`${styles.inputWrapper} ${styles.bottomChild}`}
                    >
                      <PhoneMaskInput
                        className="CheckoutInput Input Input--empty"
                        autoComplete="tel"
                        autoCorrect="off"
                        spellCheck="false"
                        id="phone"
                        name="phone"
                        type="text"
                        inputMode="tel"
                        autoCapitalize="none"
                        aria-invalid="false"
                        aria-describedby=""
                        placeholder={t("mainPage.phone")}
                      />
                    </div>

                    <div
                      className={styles.fieldError}
                      style={
                        mergeShippingDetailsErrors(touched, errors)
                          ? { opacity: 1, height: 16 }
                          : { opacity: 0, height: 0 }
                      }
                    >
                      {shippingDetailsErrors.current}
                    </div>
                  </div>
                  {/* <div style="opacity: 1; transform: none;"></div> */}
                </div>
              </div>
            </div>

            {showShippingInfo ? (
              <div className={styles.row}>
                <div className={styles.fieldGroup}>
                  <div className={styles.labelContainer}>
                    <label htmlFor="">
                      <span className={styles.label}>
                        {t("mainPage.shippingAddress.label")}
                      </span>
                    </label>

                    <div
                      className={styles.error}
                      style={
                        touched.shippingName &&
                        errors.shippingName &&
                        touched.shippingAddress &&
                        errors.shippingAddress
                          ? { opacity: 1, transform: "translateY(0)" }
                          : { opacity: 0, transform: "translateY(100%)" }
                      }
                    >
                      {t("common.required")}
                    </div>
                  </div>

                  <div className={styles.formInputGroup}>
                    <div className={styles.formInputContainer}>
                      <div
                        className={`${styles.inputWrapper} ${styles.topChild}`}
                      >
                        <Input
                          className="CheckoutInput Input Input--empty"
                          autoComplete="shipping name"
                          autoCorrect="off"
                          spellCheck="false"
                          id="shippingName"
                          name="shippingName"
                          type="text"
                          aria-label="Name"
                          placeholder={t(
                            "mainPage.shippingAddress.namePlaceholder"
                          )}
                          aria-invalid="false"
                          aria-describedby=""
                          style={{
                            zIndex:
                              touched.shippingName && errors.shippingName
                                ? 1
                                : "unset",
                          }}
                        />
                      </div>

                      <div
                        className={`${styles.inputWrapper} ${styles.bottomChild}`}
                      >
                        <Input
                          className="CheckoutInput Input Input--empty"
                          autoComplete="shipping address street-address"
                          autoCorrect="off"
                          spellCheck="false"
                          id="shippingAddress"
                          name="shippingAddress"
                          type="text"
                          aria-label="Address"
                          placeholder={t(
                            "mainPage.shippingAddress.addressPlaceholder"
                          )}
                          aria-invalid="false"
                          aria-describedby=""
                        />
                      </div>

                      {/* <div style="opacity: 1; transform: none;"></div> */}
                    </div>
                    {/* <div style="opacity: 1; transform: none;"></div> */}
                  </div>
                </div>
              </div>
            ) : null}

            <div className={styles.row}>
              <Checkbox name="termsAndConditions">
                <div>
                  <Trans i18nKey="mainPage.termsAndConditions">
                    <span></span>
                    <a href="/" rel="noopener noreferrer">
                      {/* {t("mainPage.termsAndConditions")}{" "} */}
                    </a>
                    <span></span>
                    <a href="/" rel="noopener noreferrer">
                      {/* {t("mainPage.termsAndConditions")}{" "} */}
                    </a>
                  </Trans>
                </div>
              </Checkbox>

              <div
                className={styles.fieldError}
                style={
                  touched.termsAndConditions && errors.termsAndConditions
                    ? { opacity: 1, height: 16 }
                    : { opacity: 0, height: 0 }
                }
              >
                {t("common.acceptAgreement")}
              </div>
            </div>

            <div className={styles.row}>
              <button
                className={`${styles.payButton} ${
                  isPaymentSuccessful ? styles.success : ""
                }`}
                type="submit"
                disabled={isSubmitting || isPaymentSuccessful}
              >
                {isSubmitting ? (
                  <LoadingSpinner />
                ) : isPaymentSuccessful ? (
                  <SuccessIndicator />
                ) : (
                  `${t("mainPage.pay")} ${"$55"}`
                )}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default PaymentForm;
